import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useLocation } from "react-router-dom";
import {
  Box,
  CircularProgress,
  Grid,
  Stack,
  Divider,
  Typography,
  Alert,
  useTheme,
} from "@mui/material";
import { Check, ClipboardList } from "lucide-react";

import { useAuth } from "../../../provider";
import { useTranslation } from "../../../language";
import PageProtectedLayout from "../../../components/Page/PageProtectedLayout";
import LabWorkflowSteps from "../../../components/Steps/LabWorkflowSteps";
import ShippingAddressEditor from "../../../components/Address/ShippingAddressEditor";
import ButtonsMain from "../../../components/Button/ButtonMain";
import CustomDialog from "../../../components/Dialog/DiaCompMedium";
import LabVisitAdminConfirmCard from "./LabVisitAdminConfirmCard";
import BiomaQuest_de from "./Questionnaires/BiomaQuest_de";
import BiomaQuest_en from "./Questionnaires/BiomaQuest_en";
import RecommendationsCheckIn from "./RecommendationsCheckIn";
import ActionHeader from "./ActionHeader";
import * as c from "../../../constants";

// Visit states this page is relevant for — 41 (kit/instructions + confirm-visit still
// pending) and 42 (visit already confirmed, only the questionnaire is left)
const RELEVANT_STATES = [41, 42];

function cardSx(theme, accentColor) {
  return {
    border: '1px solid',
    borderRadius: 4,
    overflow: 'hidden',
    borderLeft: `10px solid ${accentColor}`,
    ...(theme.palette.mode === 'light'
      ? { bgcolor: 'grey.C50', borderColor: 'grey.C900' }
      : { bgcolor: 'greydark.C900', borderColor: 'greydark.C400',
          backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)',
          boxShadow: '0 8px 32px rgba(0,0,0,0.6)' }),
  };
}

function NumberedItem({ number, text, accentColor, isLast, extra }) {
  const theme = useTheme();
  return (
    <>
      <Stack direction="row" spacing={2} alignItems="flex-start" sx={{ py: 1.5 }}>
        <Box
          sx={{
            flexShrink: 0, width: 28, height: 28, borderRadius: "50%",
            border: `2px solid ${accentColor}`,
            display: "flex", alignItems: "center", justifyContent: "center", mt: "2px",
          }}
        >
          <Typography variant="number4" sx={{ color: accentColor, fontWeight: 700, fontSize: "0.75rem", lineHeight: 1 }}>
            {number}
          </Typography>
        </Box>
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7 }}>
            {text}
          </Typography>
          {extra}
        </Box>
      </Stack>
      {!isLast && (
        <Divider sx={{ borderColor: theme.palette.mode === "light" ? "grey.C900" : "greydark.C400" }} />
      )}
    </>
  );
}

export default function MicrobiomePath() {
  const theme = useTheme();
  const { state } = useAuth();
  const { t, language } = useTranslation();
  const location = useLocation();
  const labVisitIdFromNav = location.state?.labVisitId;

  // ── Visit state ────────────────────────────────────────────────────────────
  const [visits, setVisits] = useState([]);
  const [currentVisit, setCurrentVisit] = useState(null);
  const [pageLoading, setPageLoading] = useState(true);
  const [confirmedOverride, setConfirmedOverride] = useState(false);

  // ── Kit state ──────────────────────────────────────────────────────────────
  const [kit, setKit] = useState(null);
  const [kitLoading, setKitLoading] = useState(true);
  const [kitError, setKitError] = useState(null);

  // ── Questionnaire dialog state ────────────────────────────────────────────
  const [dialogOpen, setDialogOpen] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const activeVisitId = useMemo(
    () => labVisitIdFromNav ?? currentVisit?.id ?? null,
    [labVisitIdFromNav, currentVisit]
  );

  const labVisit = useMemo(() => {
    if (labVisitIdFromNav) {
      return visits.find((v) => String(v.id) === String(labVisitIdFromNav)) ?? currentVisit;
    }
    return currentVisit;
  }, [labVisitIdFromNav, visits, currentVisit]);

  // ── 1. Fetch visit ───────────────────────────────────────────────────────────
  useEffect(() => {
    if (!state?.user?.id) return;
    let mounted = true;

    const fetchVisits = async () => {
      setPageLoading(true);
      try {
        if (labVisitIdFromNav) {
          const { data } = await axios.get(`${c.API_URL}/api/user-lab-visits/${labVisitIdFromNav}/`);
          if (mounted) {
            setVisits(data ? [data] : []);
            setCurrentVisit(data ?? null);
          }
        } else {
          const { data } = await axios.get(`${c.API_URL}/api/user-lab-visits/`);
          const visitsArray = Array.isArray(data) ? data : data ? [data] : [];
          const active = visitsArray.find((v) => RELEVANT_STATES.includes(v.state)) ?? null;
          if (mounted) {
            setVisits(visitsArray);
            setCurrentVisit(active);
          }
        }
      } catch (err) {
        console.error(err);
      } finally {
        if (mounted) setPageLoading(false);
      }
    };

    fetchVisits();
    return () => { mounted = false; };
  }, [state?.user?.id, labVisitIdFromNav]);

  // ── 2. Fetch latest microbiome kit ─────────────────────────────────────────
  useEffect(() => {
    let mounted = true;

    const fetchLatestKit = async () => {
      try {
        setKitLoading(true);
        const res = await axios.get(`${c.API_URL}/api/stool-sample-kits/my-latest-kit/`);
        if (mounted) {
          setKit(res.data);
          setKitError(null);
        }
      } catch (err) {
        if (!mounted) return;
        setKitError(
          err.response?.status === 404
            ? "No stool sample kit has been assigned yet."
            : "Failed to load stool sample kit."
        );
      } finally {
        if (mounted) setKitLoading(false);
      }
    };

    fetchLatestKit();
    return () => { mounted = false; };
  }, []);

  // The lab-visit-confirmation control only applies while still at state 41 — once
  // confirmed (or if we loaded straight into 42), only the questionnaire remains.
  const isConfirmed = confirmedOverride || labVisit?.state === 42;

  const accentColor = theme.palette.tertiary.C300;

  // ── Guards ───────────────────────────────────────────────────────────────
  if (pageLoading) {
    return (
      <Box display="flex" justifyContent="center" mt={6}>
        <CircularProgress />
      </Box>
    );
  }

  if (!labVisit || !activeVisitId) {
    return (
      <Box display="flex" justifyContent="center" mt={6}>
        <Typography variant="subtitle1" color="text.secondary">
          No active lab visit found. Please start a new lab visit.
        </Typography>
      </Box>
    );
  }

  return (
    <PageProtectedLayout headline={t("lab_header5")} specify={t("lab_header6")}>
      <Grid
        container
        spacing={1}
        sx={{ maxWidth: 1000, mb: 16, display: "flex", justifyContent: "center", alignItems: "center" }}
      >
        {/* ── Workflow Stepper ── */}
        <Grid size={12} sx={{ maxWidth: 800, my: 2, display: "flex", justifyContent: "center", alignItems: "center" }}>
          <LabWorkflowSteps stepNumber={2} />
        </Grid>

        {/* ── Kit card ── */}
        <Grid size={12} sx={{ mt: 6, display: "flex", justifyContent: "center", alignItems: "center" }}>
          <Box sx={{ width: "100%", maxWidth: 700 }}>
            {kitLoading ? (
              <Grid container justifyContent="center">
                <CircularProgress />
              </Grid>
            ) : kitError ? (
              <Alert severity="info">{kitError}</Alert>
            ) : kit ? (
              <Box sx={{ ...cardSx(theme, accentColor), p: 3, textAlign: "center" }}>
                <Typography variant="h6" textAlign="center" mb={2}>
                  {t("microstep_kit_header")}
                </Typography>
                <Typography variant="body2" color="text.secondary" textAlign="center" mb={2}>
                  {t("microstep_kit_note")}
                </Typography>
                <Typography variant="subtitle2" fontWeight={400}>{t("onb_quest_kit_id")}</Typography>
                <Typography fontWeight={400}>{kit.kitId}</Typography>
                <Typography variant="subtitle2" fontWeight={400} mt={2}>{t("onb_quest_act_key")}</Typography>
                <Typography fontWeight={400}>{kit.activationKey}</Typography>
                <Typography variant="body2" color="text.secondary" textAlign="center" mt={2}>
                  {t("microstep_kit_activated")}
                </Typography>
              </Box>
            ) : null}
          </Box>
        </Grid>

        {/* ── Sample instructions ── */}
        <Grid size={12} sx={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
          <Box sx={{ width: "100%", maxWidth: 600, mt: 4 }}>
            {["microstep_step1", "microstep_step2", "microstep_step3"].map((key, i) => (
              <NumberedItem
                key={key}
                number={i + 1}
                text={t(key)}
                accentColor={accentColor}
                isLast={i === 2}
                extra={
                  i === 0 ? (
                    <ShippingAddressEditor
                      labelKey="microstep_address_label"
                      promptKey="microstep_address_change_prompt"
                      emptyKey="microstep_address_empty"
                    />
                  ) : null
                }
              />
            ))}
          </Box>
        </Grid>

        {/* ── Questionnaire ── */}
        <Grid size={12} sx={{ display: "flex", justifyContent: "center", alignItems: "center", mt: 2 }}>
          <Box sx={{ width: "100%", maxWidth: 700 }}>
            <Divider sx={{ mb: 4 }} />

            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 2,
                width: "100%",
                border: "1px solid",
                borderRadius: 4,
                p: 2.5,
                ...(theme.palette.mode === "light"
                  ? { bgcolor: "grey.C50", borderColor: "grey.C900" }
                  : {
                      bgcolor: "greydark.C980",
                      borderColor: "greydark.C400",
                      backdropFilter: "blur(20px)",
                      WebkitBackdropFilter: "blur(20px)",
                      boxShadow: "0 8px 32px rgba(0,0,0,0.6)",
                    }),
              }}
            >
              <ActionHeader
                number={1}
                label={t("lab_action1_header")}
                accentColor={accentColor}
              />

              <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}>
                <Box
                  sx={{
                    flexShrink: 0,
                    width: 28,
                    height: 28,
                    borderRadius: "50%",
                    bgcolor: submitted
                      ? `${theme.palette.traffic.green}22`
                      : `${accentColor}22`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    mt: "2px",
                  }}
                >
                  {submitted ? (
                    <Check size={16} strokeWidth={3} color={theme.palette.traffic.green} />
                  ) : (
                    <ClipboardList size={16} color={accentColor} />
                  )}
                </Box>
                <Box>
                  {submitted ? (
                    <Typography variant="body1" fontWeight={700}>
                      {t("lab_micro_quest_done")}
                    </Typography>
                  ) : (
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                      {t("lab_micro_quest_desc")}
                    </Typography>
                  )}

                  {!submitted && (
                    <ButtonsMain
                      action={() => setDialogOpen(true)}
                      name={t("lab_quest_review_btn")}
                      size="small"
                      sx={{ minWidth: 0, mt: 0, px: 1.5, py: 0.25, fontSize: "0.75rem" }}
                    />
                  )}
                </Box>
              </Box>
            </Box>
          </Box>
        </Grid>

        {/* ── Recommendation check-in ── */}
        <Grid size={12} sx={{ maxWidth: 700, mt: 4, display: "flex", justifyContent: "center" }}>
          <RecommendationsCheckIn />
        </Grid>

        {/* ── Confirm lab visit (only while not yet confirmed) ── */}
        {!isConfirmed && (
          <Grid size={12} sx={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
            <Box sx={{ width: "100%", maxWidth: 700, mt: 4 }}>
              <Divider sx={{ mb: 4 }} />
              <LabVisitAdminConfirmCard
                labVisitId={activeVisitId}
                confirmState={42}
                onConfirmed={() => setConfirmedOverride(true)}
                maxWidth={700}
              />
            </Box>
          </Grid>
        )}
      </Grid>

      <CustomDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        title={t("onb_quest_form_title")}
        content={
          language === "en" ? (
            <BiomaQuest_en
              labVisitId={activeVisitId}
              nextState={isConfirmed ? 5 : 4}
              onSubmitSuccess={() => {
                setDialogOpen(false);
                setSubmitted(true);
              }}
            />
          ) : (
            <BiomaQuest_de
              labVisitId={activeVisitId}
              nextState={isConfirmed ? 5 : 4}
              onSubmitSuccess={() => {
                setDialogOpen(false);
                setSubmitted(true);
              }}
            />
          )
        }
      />
    </PageProtectedLayout>
  );
}
